import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { getClients } from '~/api/clients';
import type { ApiResponse, OptionType } from '../../../../../src/component/PaginatedSelect/type';
import type { CreateActivationFormFieldValues } from '../logics/schema';
import { createActivationSchema } from '../logics/schema';
import type { CreateActivationFormPresentation } from '../presentations';
import { usePostBulkActivationCodeCreation } from './usePostBulkActivationCodeCreation';

type PresentationProps = React.ComponentProps<typeof CreateActivationFormPresentation>;

type IUseCreateActivationForm = () => {
  state: {
    methods: UseFormReturn<CreateActivationFormFieldValues>;
    isButtonDisabled: boolean;
    searchAction: PresentationProps['searchAction'];
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
    handleClientChange: PresentationProps['onClientChange'];
  };
};

/**
 * アクティベーション追加フォームのカスタムフック
 *
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isButtonDisabled - ボタン非活性フラグ
 * @returns state.searchAction - 企業選択のページネーション対応検索関数
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 * @returns actions.handleClientChange - 企業選択変更ハンドラ
 */
export const useCreateActivationForm: IUseCreateActivationForm = () => {
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const methods = useForm<CreateActivationFormFieldValues>({
    defaultValues: {
      clientId: '',
      quantity: '',
    },
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(createActivationSchema),
  });

  const watchedClientId = methods.watch('clientId');

  const { trigger, isMutating } = usePostBulkActivationCodeCreation(
    threeLetterCode,
    Number(watchedClientId) || 0,
    {
      onSuccess: () => {
        showToast({
          message:
            '利用者を追加しました。\n※すべての利用者の登録には最大10分程度かかる場合がございます。',
          status: 'success',
        });
        navigate('/users');
      },
      onError: error => {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? '利用者の追加に失敗しました。',
          status: 'danger',
        });
      },
    }
  );

  const handleOnSubmit = methods.handleSubmit(async formValues => {
    await trigger({ count: Number(formValues.quantity) });
  });

  /**
   * 企業選択のページネーション対応検索関数
   */
  const searchAction = async (input: string, page: number): Promise<ApiResponse> => {
    try {
      const { data, headers } = await getClients(undefined, {
        name: input,
        page: page.toString(),
        enabled: '1',
      });
      const options: OptionType[] = data.clients.map(client => ({
        label: client.name,
        value: String(client.id),
      }));
      return {
        options,
        headers: { 'total-pages': headers['total-pages'] },
      };
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
      return { options: [], headers: { 'total-pages': '0' } };
    }
  };

  /**
   * 企業選択の変更ハンドラ
   */
  const handleClientChange: PresentationProps['onClientChange'] = option => {
    methods.setValue('clientId', option ? String(option.value) : '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return {
    state: {
      methods,
      isButtonDisabled: methods.formState.isSubmitting || !methods.formState.isValid || isMutating,
      searchAction,
    },
    actions: { handleOnSubmit, handleClientChange },
  };
};
